/**
 * Lusuku Image Upload Utility — Main Dashboard
 * ─────────────────────────────────────────────────────────────
 * Centralised image pipeline for ALL vendor types:
 *   Restaurant • Brand • Retail/Wholesale • Healthcare • 3PL • Adverts
 *
 * Instagram-style approach:
 *   1. Client-side validation (type + size)
 *   2. Client-side compression via canvas (resize + quality reduction)
 *   3. Cloudinary upload with eager transformations
 *
 * This guarantees professional, consistent images across all platforms
 * without burdening the vendor with strict requirements.
 *
 * USAGE:
 *   import { uploadImageToCloudinary, validateImageFile } from '../../utilities/imageUpload';
 *
 *   // In your submit handler:
 *   const result = await uploadImageToCloudinary(file, { onProgress, preset: 'lusuku-clou' });
 *   const imageUrl = result.secure_url;
 *
 * @see https://cloudinary.com/documentation/image_upload_api_reference
 */

// ── Cloudinary config ──
const CLOUD_NAME = "lusuku";
const DEFAULT_UPLOAD_PRESET = "lusuku-clou";
const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

const dashboardApiBaseUrl = () => {
    const raw = process.env.REACT_APP_SERVER_URL;
    if (raw != null && String(raw).trim() !== "") {
        return String(raw).trim().replace(/\/$/, "");
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8001";
    }
    return "";
};

const getAuthToken = () => {
    try {
        const profile = JSON.parse(localStorage.getItem("profile") || "{}");
        return profile?.token || localStorage.getItem("token") || "";
    } catch {
        return localStorage.getItem("token") || "";
    }
};

const vendorMediaRequest = async (path, body) => {
    const token = getAuthToken();
    const res = await fetch(`${dashboardApiBaseUrl()}/api/vendor-media${path}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
        const error = new Error(data.message || data.data?.message || "Vendor media request failed.");
        error.response = data;
        throw error;
    }
    return data.data || data;
};

// ── Validation limits ──
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/jpg",
];

const MB = 1024 * 1024;
/* `field` mirrors configs/vendorProfileMedia.js on the server: the property on
   the vendor document that holds this role's URL. `legacyField` is the older
   property the server still falls back to when the primary is empty (shop
   banners used to live in bannerUrl). `shortLabel` is what a delete dialog
   calls the image; the long `label` stays for upload errors. Keep the roles in
   the order a vendor form shows them. */
export const VENDOR_PROFILE_MEDIA_RULES = Object.freeze({
    restaurant: Object.freeze({
        logo: Object.freeze({ width: 588, height: 558, maxBytes: 2 * MB, label: "Restaurant logo", shortLabel: "Logo", assetPurpose: "logo", field: "imageUrl" }),
        shopBanner: Object.freeze({ width: 1440, height: 175, maxBytes: 5 * MB, label: "Restaurant shop banner", shortLabel: "Shop banner", assetPurpose: "shop_banner", field: "shopBannerUrl", legacyField: "bannerUrl" }),
        cardImage: Object.freeze({ width: 1440, height: 900, maxBytes: 5 * MB, label: "Restaurant card image", shortLabel: "Card image", assetPurpose: "featured", field: "cardImageUrl" }),
    }),
    retailer_wholesaler: Object.freeze({
        logo: Object.freeze({ width: 588, height: 558, maxBytes: 2 * MB, label: "Retailer/wholesaler logo", shortLabel: "Logo", assetPurpose: "logo", field: "imageUrl" }),
        shopBanner: Object.freeze({ width: 1440, height: 175, maxBytes: 5 * MB, label: "Retailer/wholesaler shop banner", shortLabel: "Shop banner", assetPurpose: "shop_banner", field: "shopBannerUrl", legacyField: "bannerUrl" }),
        cardImage: Object.freeze({ width: 1280, height: 720, maxBytes: 5 * MB, label: "Retailer/wholesaler card image", shortLabel: "Card image", assetPurpose: "featured", field: "cardImageUrl" }),
    }),
    healthcare: Object.freeze({
        logo: Object.freeze({ width: 1024, height: 1024, maxBytes: 2 * MB, label: "Healthcare provider logo", shortLabel: "Logo", assetPurpose: "logo", field: "imageUrl" }),
        cardImage: Object.freeze({ width: 1200, height: 800, maxBytes: 5 * MB, label: "Healthcare landing-page card image", shortLabel: "Card image", assetPurpose: "featured", field: "bannerUrl" }),
        shopBanner: Object.freeze({ width: 1440, height: 175, maxBytes: 5 * MB, label: "Healthcare shop banner", shortLabel: "Shop banner", assetPurpose: "shop_banner", field: "shopBannerUrl" }),
    }),
});

export const getVendorProfileMediaRule = (vendorType, mediaRole) =>
    VENDOR_PROFILE_MEDIA_RULES[String(vendorType || "").toLowerCase()]?.[mediaRole] || null;

export const readImageDimensions = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("The selected file could not be read as an image."));
    };
    image.src = objectUrl;
});

export const validateExactVendorProfileMedia = async (file, vendorType, mediaRole) => {
    const rule = getVendorProfileMediaRule(vendorType, mediaRole);
    if (!rule) throw new Error("This vendor media role is not configured.");

    const basic = validateImageFile(file);
    if (!basic.valid) throw new Error(basic.error);
    if (file.size > rule.maxBytes) {
        throw new Error(`${rule.label} must be no larger than ${Math.round(rule.maxBytes / MB)} MB.`);
    }

    const dimensions = await readImageDimensions(file);
    if (dimensions.width !== rule.width || dimensions.height !== rule.height) {
        throw new Error(
            `${rule.label} must be exactly ${rule.width}x${rule.height}px; received ${dimensions.width}x${dimensions.height}px.`
        );
    }
    return { ...rule, ...dimensions };
};

// ── Client-side compression targets ──
const COMPRESS_MAX_WIDTH = 1600;
const COMPRESS_MAX_HEIGHT = 1600;
const COMPRESS_QUALITY = 0.82; // JPEG 82% = excellent quality, ~60-70% smaller

/**
 * Validate a File object before processing.
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateImageFile = (file) => {
    if (!file) {
        return { valid: false, error: "No file selected." };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
        return {
            valid: false,
            error: `Unsupported format "${file.type.split("/")[1]?.toUpperCase() || "unknown"
                }". Please use JPG, PNG, or WebP.`,
        };
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return {
            valid: false,
            error: `Image is too large (${sizeMB}MB). Maximum allowed is ${MAX_FILE_SIZE_MB}MB.`,
        };
    }

    return { valid: true };
};

/**
 * Compress an image file in the browser using canvas.
 * - Resizes to fit within COMPRESS_MAX_WIDTH × COMPRESS_MAX_HEIGHT
 * - Outputs JPEG at COMPRESS_QUALITY
 * - Maintains aspect ratio
 *
 * @param {File} file - The original image file
 * @returns {Promise<Blob>} - Compressed image blob
 */
export const compressImage = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
            img.onload = () => {
                try {
                    let { width, height } = img;

                    if (width > COMPRESS_MAX_WIDTH || height > COMPRESS_MAX_HEIGHT) {
                        const ratio = Math.min(
                            COMPRESS_MAX_WIDTH / width,
                            COMPRESS_MAX_HEIGHT / height
                        );
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext("2d");
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error("Canvas compression failed."));
                            }
                        },
                        "image/jpeg",
                        COMPRESS_QUALITY
                    );
                } catch (err) {
                    reject(err);
                }
            };

            img.onerror = () =>
                reject(new Error("Failed to load image for compression."));
            img.src = e.target.result;
        };

        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsDataURL(file);
    });
};

/**
 * Upload an image to Cloudinary with eager transformations.
 *
 * Cloudinary stores the original AND pre-generates optimised versions:
 *   - hero:      1200×800  (restaurant/vendor banners)
 *   - card:       600×400  (listing cards, product cards)
 *   - thumbnail:  300×300  (small grids, search results)
 *
 * @param {File}     file               - The raw File from <input type="file">
 * @param {object}   [options]          - Upload options
 * @param {Function} [options.onProgress] - Progress callback (0-100)
 * @param {string}   [options.preset]     - Cloudinary upload preset (default: 'lusuku-clou')
 * @param {boolean}  [options.skipCompression] - Skip client-side compression
 * @param {boolean}  [options.skipValidation]  - Skip validation (for special cases)
 * @param {object}   [options.vendorMedia] - Optional quota-aware media context:
 *   { vendorType, vendorId, assetPurpose, entityType, entityId, source,
 *     pendingAttachment }
 * @returns {Promise<{ secure_url, public_id, width, height, format, bytes }>}
 */

/**
 * Upload any file (PDF, doc, video, audio, etc.) to Cloudinary without image
 * compression, using the auto resource type. Returns { secure_url, format, bytes }.
 * Note: the unsigned preset must allow non-image (raw/video/auto) uploads —
 * pass options.preset to route e.g. videos through a video-enabled preset.
 * Supports options.onProgress(percent) for upload progress (0-100).
 * Failures throw with Cloudinary's real error message so callers can surface it.
 */
export const MAX_DOC_SIZE_BYTES = 16 * 1024 * 1024;
export const uploadFileToCloudinary = async (file, options = {}) => {
  const preset = options.preset || DEFAULT_UPLOAD_PRESET;
  const { onProgress } = options;
  if (!file) throw new Error("No file selected.");
  if (file.size > MAX_DOC_SIZE_BYTES) {
    throw new Error(`File is too large (max ${(MAX_DOC_SIZE_BYTES / 1024 / 1024) | 0}MB).`);
  }
  const url = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`;
  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("upload_preset", preset);

  // XMLHttpRequest instead of fetch so we can report upload progress.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ secure_url: data.secure_url, format: data.format, bytes: data.bytes });
        } catch {
          reject(new Error("Invalid response from the upload server."));
        }
      } else {
        // Bubble up Cloudinary's own message (e.g. "Resource type not allowed
        // by the upload preset") instead of a generic failure.
        let message = `Upload failed (${xhr.status}). Please try again.`;
        try {
          const payload = JSON.parse(xhr.responseText || "{}");
          message = payload?.error?.message || payload?.message || message;
        } catch {
          // Keep the status-based fallback.
        }
        reject(new Error(message));
      }
    };

    xhr.onerror = () => reject(new Error("Network error during upload. Check your connection."));
    xhr.ontimeout = () => reject(new Error("Upload timed out. Try a smaller file."));

    xhr.timeout = 180000; // videos/docs can be large — allow up to 3 minutes
    xhr.send(formData);
  });
};

export const uploadImageToCloudinary = async (file, options = {}) => {
    const {
        onProgress,
        preset = DEFAULT_UPLOAD_PRESET,
        skipCompression = false,
        skipValidation = false,
        vendorMedia = null,
        // A server-signed upload ({ uploadUrl, apiKey, params }) from an
        // intent endpoint. Signed uploads do not go through an upload preset,
        // so the preset's incoming size limit (2000px on `lusuku-clou`) does
        // not apply. Used by the Ad Center for 3159px desktop artwork.
        signed = null,
    } = options;

    // 0. Handle Data URLs (Base64) — convert to File object for the pipeline
    // This allows cropped images or images from canvas (strings) to be processed
    if (typeof file === "string" && file.startsWith("data:")) {
        try {
            const response = await fetch(file);
            const blob = await response.blob();
            const mime = blob.type || "image/jpeg";
            const ext = mime.split("/")[1] || "jpg";
            file = new File([blob], `upload.${ext}`, { type: mime });
        } catch (err) {
            console.error("[ImageUpload] DataURL conversion failed:", err);
            // Fallback: stay as string, Cloudinary accepts strings, 
            // but validation/compression will likely fail.
        }
    }

    // 1. Validate
    if (!skipValidation) {
        const validation = validateImageFile(file);
        if (!validation.valid) {
            throw new Error(validation.error);
        }
    }

    // 2. Compress client-side
    let processedImage = file;
    if (!skipCompression) {
        try {
            processedImage = await compressImage(file);
        } catch {
            // Fallback: use original if compression fails (e.g., HEIC)
            processedImage = file;
        }
    }

    let mediaIntent = null;
    if (vendorMedia) {
        mediaIntent = await vendorMediaRequest("/upload-intent", {
            ...vendorMedia,
            fileName: file.name || "product.jpg",
            expectedBytes: processedImage?.size || file?.size || 0,
        });
        if (mediaIntent?.allowed === false) {
            throw new Error(mediaIntent.message || "You need more image space before uploading.");
        }
    }

    // 3. Build FormData with Cloudinary eager transformations
    const formData = new FormData();
    formData.append("file", processedImage, file.name || "product.jpg");
    const signedUpload = signed?.params ? signed : mediaIntent?.cloudinary;
    if (signedUpload?.params) {
        Object.entries(signedUpload.params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) formData.append(key, value);
        });
        if (signedUpload.apiKey) {
            formData.append("api_key", signedUpload.apiKey);
        }
    } else {
        formData.append("upload_preset", preset);
    }
    // cloud_name is already in the UPLOAD_URL, so we don't need to append it to FormData
    // as it might be rejected by strict unsigned upload whitelists.

    // Note: 'eager' and 'eager_async' are restricted in unsigned uploads.
    // We achieve the same result using on-the-fly transformations via getOptimizedUrl().
    // If pre-generation is required, you must configure 'Eager Transformations'
    // directly on the Cloudinary Upload Preset (lusuku-clou) in the dashboard.

    // 4. Upload with XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", signedUpload?.uploadUrl || UPLOAD_URL, true);

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const percent = Math.round((e.loaded / e.total) * 100);
                    onProgress(percent);
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const data = JSON.parse(xhr.responseText);
                    const result = {
                        secure_url: data.secure_url,
                        public_id: data.public_id,
                        width: data.width,
                        height: data.height,
                        format: data.format,
                        bytes: data.bytes,
                    };

                    if (mediaIntent?.intentId) {
                        vendorMediaRequest("/upload-complete", {
                            intentId: mediaIntent.intentId,
                            cloudinaryPublicId: data.public_id,
                            cloudinaryUrl: data.secure_url,
                            bytes: data.bytes,
                            width: data.width,
                            height: data.height,
                            format: data.format,
                            resourceType: data.resource_type || "image",
                        })
                            .then((registered) => {
                                resolve({ ...result, vendorMedia: registered });
                            })
                            .catch((err) => {
                                console.warn("[ImageUpload] vendor media registration failed:", err?.message || err);
                                reject(new Error(err?.message || "Image uploaded, but Lusuku could not register it. Please retry."));
                            });
                        return;
                    }

                    resolve(result);
                } catch {
                    reject(new Error("Invalid response from image server."));
                }
            } else {
                let message = `Upload failed (${xhr.status}). Please try again.`;
                try {
                    const payload = JSON.parse(xhr.responseText || "{}");
                    message = payload?.error?.message || payload?.message || message;
                } catch {
                    // Keep the status-based fallback.
                }
                reject(
                    new Error(message)
                );
            }
        };

        xhr.onerror = () =>
            reject(new Error("Network error during upload. Check your connection."));
        xhr.ontimeout = () =>
            reject(new Error("Upload timed out. Try a smaller image."));

        xhr.timeout = 60000;
        xhr.send(formData);
    });
};

export const uploadExactVendorProfileMedia = async (file, options = {}) => {
    const { vendorType, mediaRole, vendorId, entityType, entityId, source = "dashboard", onProgress } = options;
    const rule = await validateExactVendorProfileMedia(file, vendorType, mediaRole);
    const result = await uploadImageToCloudinary(file, {
        skipCompression: true,
        onProgress,
        vendorMedia: {
            vendorType,
            vendorId,
            mediaRole,
            assetPurpose: rule.assetPurpose,
            entityType,
            entityId,
            source,
        },
    });

    if (
        Number(result.width) !== rule.width ||
        Number(result.height) !== rule.height ||
        Number(result.bytes) > rule.maxBytes
    ) {
        throw new Error(
            `${rule.label} was not stored exactly as supplied. The upload was rejected.`
        );
    }
    return result;
};

/**
 * Helper — get optimised Cloudinary URL for a specific display context.
 *
 * @param {string} url                                     - Raw Cloudinary URL
 * @param {'hero'|'card'|'thumbnail'} [preset='card']      - Which preset to use
 * @returns {string} - Optimised URL
 */
export const getOptimizedUrl = (url, preset = "card") => {
    if (!url || !url.includes("cloudinary.com") || !url.includes("/upload/")) {
        return url;
    }

    const transforms = {
        hero: "c_fill,g_auto,w_1200,h_800,q_auto:good,f_auto",
        card: "c_fill,g_auto,w_600,h_400,q_auto,f_auto",
        thumbnail: "c_fill,g_auto,w_300,h_300,q_auto:eco,f_auto",
    };

    const transform = transforms[preset] || transforms.card;
    return url.replace("/upload/", `/upload/${transform}/`);
};

export default uploadImageToCloudinary;
